import type { ComponentType, ReactNode } from 'react';
import { Suspense } from 'react';
import { LoadingScreen } from '../LoadingScreen/LoadingScreen';

export type LoadableFallback = ReactNode | (() => ReactNode);

/**
 * HOC that wraps a component with React Suspense and an optional fallback.
 * Use with lazy()-loaded components for code-splitting. When fallback is a
 * function, it is called at render time (e.g. for environment checks).
 *
 * @example
 * const Page = Loadable(lazy(() => import('./Page')));
 * @example
 * const Page = Loadable(lazy(() => import('./Page')), () => inTest ? null : <LoadingScreen />);
 */
export function Loadable<P extends object>(
  Component: ComponentType<P>,
  fallback?: LoadableFallback
): (props: P) => ReactNode {
  return function LoadableWrapper(props: P) {
    const resolvedFallback =
      typeof fallback === 'function' ? fallback() : fallback ?? <LoadingScreen />;
    return (
      <Suspense fallback={resolvedFallback}>
        <Component {...props} />
      </Suspense>
    );
  };
}
